import * as React from 'react';
import { makeStyles } from '@material-ui/core';
import useThresholdChart from './useThresholdBarChart';
import clsx from 'clsx';
import { curveStep, line, select } from 'd3';
import { fontFamilies } from '../../theme/text';
import ThresholdTooltip, { ThresholdTooltipData } from './threshold-tooltip';
import { blue } from '../../theme/colors';

/**
 * A single event represent by a bar in the chart.
 */
export interface ThresholdEvent {
  /** timestamp of the test run */
  date: Date;
  /** whether the test passes the threshold test */
  isPassing: boolean;
  /** the numerical result of the test compared to the threshold */
  amount: number;
  /** the comparison operator used in the test */
  comparative: string;
  /** the threshold value amount is compared against */
  threshold: number;
}

/**
 * Display options
 */
export interface ThresholdChartOptions {
  showXAxis?: boolean;
  showDots?: boolean;
  showYValues?: boolean;
  barWidth?: number;
  svgWidth?: number;
  svgHeight?: number;
  /** Whether the max date of the x axis is now or the latest date in the dataset */
  relativeToNow?: boolean;
}

const defaultOptions: ThresholdChartOptions = {
  showXAxis: true,
  showDots: true,
  showYValues: true,
  svgWidth: 800,
  svgHeight: 200,
  relativeToNow: false
};

export interface ThresholdChartProps {
  data: ThresholdEvent[];
  options?: ThresholdChartOptions;
  containerClassName?: string;
}

const useStyles = makeStyles({
  root: {
    width: '100%',
    height: '100%'
  },
  svg: {
    fontFamily: fontFamilies.body,
    height: '100%',
    width: '100%'
  },
  hoverMask: {
    visibility: 'hidden',
    pointerEvents: 'all',
    cursor: 'crosshair'
  },
  hoverBar: {
    pointerEvents: 'all',
    cursor: 'crosshair',
    '& .hover:hover': {
      fill: 'rgba(0,0,0,.2)'
    }
  }
});

function getBarWidth(options: ThresholdChartOptions, dataLength: number) {
  let barWidth: number;
  if (options.barWidth) {
    barWidth = options.barWidth;
  } else if (dataLength < 10) {
    barWidth = 12;
  } else if (dataLength < 40) {
    barWidth = 8;
  } else if (dataLength < 80) {
    barWidth = 6;
  } else {
    barWidth = 4;
  }
  return barWidth;
}

/**
 *  ## Threshold Bar Chart
 *
 * Renders a combined bar/line chart showing a series of threshold test results
 * as bars and the threshold value overlayed as a line.
 */
const ThresholdBarChart = ({ data, options, containerClassName }: ThresholdChartProps) => {
  const classes = useStyles();
  const xAxisRef = React.useRef<SVGGElement>();
  const yAxisRef = React.useRef<SVGGElement>();
  const barsRef = React.useRef<SVGGElement>();
  const lineRef = React.useRef<SVGGElement>();
  const outOfBoundsRef = React.useRef<SVGGElement>();
  const hoverRef = React.useRef<SVGGElement>();
  const dotsRef = React.useRef<SVGGElement>();
  const [tooltip, setTooltip] = React.useState<ThresholdTooltipData | null>();

  const localOptions: ThresholdChartOptions = React.useMemo(() => {
    if (options) {
      const tempOptions = { ...defaultOptions };
      for (const option in options) {
        tempOptions[option] = options[option];
      }
      return tempOptions;
    } else {
      return defaultOptions;
    }
  }, [options]);

  const { margin, x, y, xAxis, yAxis } = useThresholdChart(data, localOptions);

  /**
   * Render X Axis
   */
  React.useEffect(() => {
    if (localOptions.showXAxis) {
      select(xAxisRef.current).call(xAxis);
    } else if (!localOptions.showXAxis && xAxisRef.current && xAxisRef.current.firstChild) {
      // if showXAxis changes to false, remove axis
      while (xAxisRef.current.firstChild) {
        xAxisRef.current.removeChild(xAxisRef.current.firstChild);
      }
    }
  }, [xAxis, localOptions]);

  /**
   * Render Y Axis
   */
  React.useEffect(() => {
    select(yAxisRef.current).call(yAxis);
  }, [yAxis]);

  /**
   * Render Bars, lines, Dots
   */
  React.useEffect(() => {
    const pass = '#1F82BF';
    const fail = '#ed6666';

    const barWidth = getBarWidth(localOptions, data.length);

    select(barsRef.current)
      .selectAll('rect')
      .data(data)
      .join('rect')
      .attr('fill', (d) => (d.isPassing ? pass : fail))
      .attr('x', (d) => x(d.date) - barWidth / 2)
      .attr('y', (d) => y(d.amount))
      .attr('height', (d) => y(0) - y(d.amount))
      .attr('width', barWidth);

    select(outOfBoundsRef.current)
      .selectAll('polygon')
      .data(data.filter((d) => y(d.amount) == margin.top))
      .join('polygon')
      .attr('fill', fail)
      .attr('points', (d) => {
        const xcenter = x(d.date);
        return `${xcenter - barWidth}, ${margin.top} ${xcenter}, ${margin.top - barWidth - 2} ${
          xcenter + barWidth
        }, ${margin.top}`;
      });

    while (lineRef.current.firstChild) {
      lineRef.current.removeChild(lineRef.current.firstChild);
    }

    select(lineRef.current)
      .append('path')
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', blue[300])
      .attr('stroke-width', 2)
      .attr(
        'd',
        line<ThresholdEvent>()
          .defined((d) => !isNaN(d.amount))
          .curve(curveStep)
          .x((d) => x(d.date))
          .y((d) => y(d.threshold))
      );

    select(hoverRef.current)
      .selectAll('rect')
      .data(data)
      .join('rect')
      .attr('x', (d, i) => x(d.date) - barWidth / 2)
      .attr('y', margin.top)
      .attr('height', localOptions.svgHeight - margin.top - margin.bottom)
      .attr('width', barWidth)
      .attr('fill', 'transparent')
      .attr('class', 'hover')
      .on('mouseenter', (e, d) => {
        setTooltip({
          x: x(d.date) - 4,
          y: margin.top,
          date: d.date,
          isPassing: d.isPassing,
          amount: d.amount,
          comparative: d.comparative,
          threshold: d.threshold
        });
      })
      .on('mouseleave', () => {
        setTooltip(null);
      });

    if (localOptions.showDots) {
      let dotWidth: number;
      if (data.length < 40) {
        dotWidth = 8;
      } else if (data.length < 80) {
        dotWidth = 6;
      } else {
        dotWidth = 4;
      }
      select(dotsRef.current)
        .selectAll('circle')
        .data(data)
        .join('circle')
        .attr('cx', (d) => x(d.date))
        .attr('cy', localOptions.svgHeight - margin.bottom + 8)
        .attr('r', dotWidth / 2)
        .attr('fill', (d) => (d.isPassing ? pass : fail))
        .attr('class', 'dot');
    } else if (!localOptions.showDots && dotsRef.current && dotsRef.current.firstChild) {
      // if dots change to false, remove dots
      while (dotsRef.current.firstChild) {
        dotsRef.current.removeChild(dotsRef.current.firstChild);
      }
    }
  }, [x, y, data, localOptions]);

  return (
    <div className={clsx(classes.root, containerClassName)}>
      <svg viewBox={`0 0 ${localOptions.svgWidth} ${localOptions.svgHeight}`}>
        <g ref={yAxisRef} />
        {localOptions.showXAxis && <g ref={xAxisRef} />}
        <g ref={barsRef} />
        <g ref={lineRef} />
        {localOptions.showDots && <g ref={dotsRef} />}
        <g ref={outOfBoundsRef} />
        <rect
          x={margin.left}
          y={margin.top}
          width={localOptions.svgWidth - margin.top - margin.left}
          height={localOptions.svgHeight - margin.top - margin.bottom}
          className={classes.hoverMask}
        />
        <g ref={hoverRef} className={classes.hoverBar}></g>
        {tooltip && <ThresholdTooltip data={tooltip} svgWidth={localOptions.svgWidth} />}
      </svg>
    </div>
  );
};

export default ThresholdBarChart;
